import React from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Sparkles, Clock, MessageSquare, Mail, Receipt, CalendarCheck, PackageCheck,
  Send, ArrowRight, Loader2, CheckCircle2, ChevronRight, PartyPopper,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { api, apiErrorMessage } from "@/lib/api";

const META = {
  leads_nuevos:      { icon: Sparkles,      cls: "text-primary",     ring: "border-primary/30 bg-primary/5" },
  seguimientos:      { icon: Clock,         cls: "text-amber-600",   ring: "border-amber-200 bg-amber-50/40" },
  mensajes_clientes: { icon: MessageSquare, cls: "text-blue-600",    ring: "border-blue-200 bg-blue-50/40" },
  mail_reservas:     { icon: Mail,          cls: "text-violet-600",  ring: "border-violet-200 bg-violet-50/40" },
  facturas_pedir:    { icon: Receipt,       cls: "text-emerald-600", ring: "border-emerald-200 bg-emerald-50/40" },
  reconfirmaciones:  { icon: CalendarCheck, cls: "text-teal-600",    ring: "border-teal-200 bg-teal-50/40" },
  viajes_cerrar:     { icon: PackageCheck,  cls: "text-rose-600",    ring: "border-rose-200 bg-rose-50/40" },
  travel_docs:       { icon: Send,          cls: "text-indigo-600",  ring: "border-indigo-200 bg-indigo-50/40" },
};
const ACTION_LABEL = { close_trip: "Mover a Cerrado", mark_docs_sent: "Marcar enviados" };

export default function PanelPendientes() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["panel-pendientes"],
    queryFn: async () => (await api.get("/panel/pendientes")).data,
    refetchInterval: 60000,
  });
  const groups = data?.groups || [];

  const actMut = useMutation({
    mutationFn: async ({ action, id }) => {
      const body = action === "close_trip" ? { status: "cerrado" } : { travel_docs: true };
      await api.put(`/trips/${id}`, body);
      return action;
    },
    onSuccess: (action) => {
      toast.success(action === "close_trip" ? "Viaje movido a Cerrado" : "Travel documents marcados como enviados");
      qc.invalidateQueries({ queryKey: ["panel-pendientes"] });
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  const go = (g) => { if (g.external) window.location.assign(g.link); else navigate(g.link); };

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2" data-testid="panel-pendientes-loading">
        <Skeleton className="h-44 w-full" /><Skeleton className="h-44 w-full" />
      </div>
    );
  }

  if (groups.length === 0) {
    return (
      <Card data-testid="panel-pendientes-empty">
        <CardContent className="flex flex-col items-center gap-2 py-12 text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600"><PartyPopper className="h-6 w-6" /></div>
          <p className="font-semibold">¡Todo al día!</p>
          <p className="text-sm text-muted-foreground">No tienes acciones pendientes ahora mismo.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2" data-testid="panel-pendientes">
      {groups.map((g) => {
        const m = META[g.key] || META.seguimientos;
        const Icon = m.icon;
        return (
          <Card key={g.key} className={m.ring} data-testid={`pendientes-${g.key}`}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="flex items-center gap-2 text-sm">
                <Icon className={`h-4 w-4 ${m.cls}`} />
                {g.label}
                <Badge variant="secondary" data-testid={`pendientes-count-${g.key}`}>{g.count}</Badge>
              </CardTitle>
              <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => go(g)} data-testid={`pendientes-go-${g.key}`}>
                Ir a <ArrowRight className="ml-1 h-3.5 w-3.5" />
              </Button>
            </CardHeader>
            <CardContent className="space-y-1.5">
              {g.items.map((it) => {
                const busy = actMut.isPending && actMut.variables?.id === it.id;
                return (
                  <div key={it.id} className="flex items-center gap-2 rounded-lg border bg-card px-2.5 py-2" data-testid={`pendientes-item-${it.id}`}>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium">{it.title}</p>
                      {it.subtitle && <p className="truncate text-xs text-muted-foreground">{it.subtitle}</p>}
                      {it.meta && <p className="truncate text-[10px] text-muted-foreground">{it.meta}</p>}
                    </div>
                    {g.action ? (
                      <Button size="sm" variant="outline" className="h-7 shrink-0 text-[11px]"
                        disabled={busy}
                        onClick={() => actMut.mutate({ action: g.action, id: it.id })}
                        data-testid={`pendientes-action-${it.id}`}>
                        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          : g.action === "close_trip" ? <CheckCircle2 className="mr-1 h-3.5 w-3.5" /> : <Send className="mr-1 h-3.5 w-3.5" />}
                        {ACTION_LABEL[g.action]}
                      </Button>
                    ) : (
                      <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                    )}
                  </div>
                );
              })}
              {g.count > g.items.length && (
                <button className="w-full pt-1 text-center text-[11px] text-muted-foreground hover:text-foreground"
                  onClick={() => go(g)} data-testid={`pendientes-more-${g.key}`}>
                  +{g.count - g.items.length} más…
                </button>
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
